import {
  FormatPersonName,
  MapFamilyMembers,
  ValueType,
  parseFormValue,
  vendorContactSearchInfo,
  vendorSearchInfo
} from './formDataConstants';
import { enumColumn, getEnumColumns } from 'modules/common/enumTableColumn';

// Helper function to calculate age from birthday
const calculateAge = (birthday) => {
  if (!birthday) return null;
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const createInternationalTrainingRows = (formDataList, familyMembers) => {
  if (!Array.isArray(formDataList)) return [];

  return formDataList.map(data => {
    const studentId = data.studentId;
    const student = familyMembers?.find(m => m.familyMemberId === studentId);
    const age = student?.dateOfBirth ? calculateAge(student.dateOfBirth) : null;
    const ageString = age !== null ? `${age} years` : '-';

    return {
      ...data,
      contactId: data.schoolingVendorContactId,
      vendorId: data.schoolingVendorId,
      tuitionAmount: parseFormValue(data.tuitionAmount, ValueType.FloatType),
      costOfBooks: parseFormValue(data.costOfBooks, ValueType.FloatType),
      miscellaneousCost: parseFormValue(data.miscellaneousCost, ValueType.FloatType),
      studentsAge: ageString, // Keep as is from DB, will be calculated for display
      orderedDate: parseFormValue(data.orderedDate, ValueType.DateTimeType),
      estSchoolStartDate: parseFormValue(data.estSchoolStartDate, ValueType.DateTimeType),
      estSchoolEndDate: parseFormValue(data.estSchoolEndDate, ValueType.DateTimeType),
      estPaymentStartDate: parseFormValue(data.estPaymentStartDate, ValueType.DateTimeType),
      estPaymentEndDate: parseFormValue(data.estPaymentEndDate, ValueType.DateTimeType),
      schoolPreferences: data.schoolPreferences ?? ''
    }
  });
};

export const createInternationalTrainingPostRequest = (formData, globalId) => {
  const request = { ...formData };
  request.globalAssignmentId = globalId;
  request.modifiedBy = 1; // TODO: replace with actual user id
  request.modifiedOn = new Date().toISOString();
  request.addedBy = 1; // TODO: replace with actual user id
  request.addedOn = new Date().toISOString();
  parseSchoolingServiceFormValues(request, formData);
  return request;
};

export const createInternationalTrainingPutRequest = (originalFormData, newFormData, globalId) => {
  const request = { ...newFormData };
  request.globalAssignmentId = globalId;
  request.globalIntlSchoolingServiceId = originalFormData.globalIntlSchoolingServiceId;
  request.modifiedBy = 1; // TODO: replace with actual user id
  request.modifiedOn = new Date().toISOString();
  request.addedBy = originalFormData.addedBy;
  request.addedOn = originalFormData.addedOn;
  parseSchoolingServiceFormValues(request, newFormData);
  return request;
};

export const createInternationalSchoolingColumns = (vendors, vendorContacts, familyMembers, enumerations, countries, allVendors) => {
  const schoolingLevels = enumerations?.schoolingLevels?.length
    ? enumerations.schoolingLevels.filter(s => s.isActive === true)
    : [{ id: '00000000-0000-0000-0000-000000000000', title: '' }];
  const { schoolingLevelColumn } = getEnumColumns(enumerations);

  return [
    {
      ...enumColumn('studentId', familyMembers, { valueKey: 'familyMemberId', label: FormatPersonName }),
      header: 'Family Member',
      type: 'dropdown-v2',
      required: true,
      options: MapFamilyMembers(familyMembers),
      size: 65,
      Cell: ({ cell }) => cell.getValue() || '-'
    },
    {
      ...enumColumn('vendorId', allVendors, { valueKey: 'vendorId', label: 'companyName' }),
      header: 'Vendor (School)',
      nameKey: 'companyName',
      searchInfo: vendorSearchInfo(vendors),
      type: 'advanced-search',
      required: true,
      size: 65
    },
    {
      ...enumColumn('contactId', vendorContacts, { valueKey: 'contactId', label: FormatPersonName }),
      header: 'Vendor Contact',
      nameKey: 'fullName',
      searchInfo: vendorContactSearchInfo(vendorContacts),
      type: 'advanced-search',
      required: false,
      size: 65,
      Cell: ({ cell }) => cell.getValue() || '-'
    },
    {
      accessorKey: 'studentsAge',
      header: 'Student Age',
      type: 'text',
      hideInTable: false,
      size: 65,
      readOnly: true,
      disabled: true,
      Cell: ({ cell, row }) => {
        const studentId = row.original.studentId;
        const student = familyMembers?.find(m => m.familyMemberId === studentId);
        const age = student?.dateOfBirth ? calculateAge(student.dateOfBirth) : null;
        return age !== null ? `${age} years` : '-';
      },
      getValue: (row) => {
        const studentId = row.studentId;
        const student = familyMembers?.find(m => m.familyMemberId === studentId);
        const age = student?.dateOfBirth ? calculateAge(student.dateOfBirth) : null;
        return age !== null ? `${age} years` : '';
      }
    },
    {
      ...schoolingLevelColumn('schoolingLevelId'),
      header: 'Schooling Level',
      type: 'dropdown-v2',
      options: schoolingLevels,
      required: true,
      size: 65
    },
    { accessorKey: 'tuitionAmount', header: 'Tuition Amount', type: 'number', size: 65 },
    { accessorKey: 'costOfBooks', header: 'Cost of Books', type: 'number', hideInTable: true, size: 65 },
    { accessorKey: 'miscellaneousCost', header: 'Miscellaneous Cost', type: 'number', hideInTable: true, size: 65 },
    { accessorKey: 'tuitionPaymentTerms', header: 'Tuition Payment Terms', type: 'text', hideInTable: true, size: 65 },
    { accessorKey: 'program', header: 'Program', type: 'text', hideInTable: true, size: 65 },
    {
      accessorKey: 'orderedDate',
      header: 'Ordered Date',
      type: 'date',
      hideInTable: false,
      size: 65,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'estSchoolStartDate',
      header: 'Estimated School Start Date',
      type: 'date',
      hideInTable: false,
      size: 65,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'estSchoolEndDate',
      header: 'Estimated School End Date',
      type: 'date',
      hideInTable: false,
      size: 65,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'estPaymentStartDate',
      header: 'Estimated Payment Start Date',
      type: 'date',
      hideInTable: false,
      size: 65,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'estPaymentEndDate',
      header: 'Estimated Payment End Date',
      type: 'date',
      hideInTable: false,
      size: 65,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'schoolPreferences',
      header: 'School Preferences',
      type: 'text',
      hideInTable: false,
      size: 65
    }
  ];
};

const parseSchoolingServiceFormValues = (currentRequest, v) => {
  currentRequest.studentId = v.studentId ?? null;
  currentRequest.schoolingVendorId = v.vendorId ?? null;
  currentRequest.schoolingVendorContactId = v.contactId ?? null;
  currentRequest.schoolingLevelId = v.schoolingLevelId ?? '00000000-0000-0000-0000-000000000000';
  currentRequest.tuitionAmount = parseFormValue(v.tuitionAmount, ValueType.FloatType);
  currentRequest.costOfBooks = parseFormValue(v.costOfBooks, ValueType.FloatType);
  currentRequest.miscellaneousCost = parseFormValue(v.miscellaneousCost, ValueType.FloatType);
  // studentsAge is calculated and displayed only, not saved - remove from request or set to null
  currentRequest.studentsAge = null;
  currentRequest.tuitionPaymentTerms = v.tuitionPaymentTerms ?? '';
  currentRequest.program = v.program ?? '';
  currentRequest.orderedDate = parseFormValue(v.orderedDate, ValueType.DateTimeType);
  currentRequest.estSchoolStartDate = parseFormValue(v.estSchoolStartDate, ValueType.DateTimeType);
  currentRequest.estSchoolEndDate = parseFormValue(v.estSchoolEndDate, ValueType.DateTimeType);
  currentRequest.estPaymentStartDate = parseFormValue(v.estPaymentStartDate, ValueType.DateTimeType);
  currentRequest.estPaymentEndDate = parseFormValue(v.estPaymentEndDate, ValueType.DateTimeType);
  currentRequest.schoolPreferences = v.schoolPreferences ?? '';
};
